import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  disableAnalytics,
  initializeAnalytics,
  isMixpanelReady,
} from "../utils/Analytics";

export const ANALYTICS_CONSENT_STORAGE_KEY = "lettucetalk.analyticsConsent.v1";
export const ANALYTICS_CONSENT_VALUES = Object.freeze({
  UNKNOWN: "unknown",
  GRANTED: "granted",
  DENIED: "denied",
});

const AnalyticsConsentContext = createContext(null);

function readStoredConsent() {
  if (typeof window === "undefined") return ANALYTICS_CONSENT_VALUES.UNKNOWN;

  try {
    const storedValue = window.localStorage.getItem(
      ANALYTICS_CONSENT_STORAGE_KEY
    );
    return Object.values(ANALYTICS_CONSENT_VALUES).includes(storedValue)
      ? storedValue
      : ANALYTICS_CONSENT_VALUES.UNKNOWN;
  } catch {
    return ANALYTICS_CONSENT_VALUES.UNKNOWN;
  }
}

function writeStoredConsent(value) {
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, value);
  } catch {
    // A storage failure should not block the product.
  }
}

export function AnalyticsConsentProvider({ children }) {
  const [consent, setConsent] = useState(readStoredConsent);
  const [analyticsReady, setAnalyticsReady] = useState(false);
  const [mixpanelReady, setMixpanelReady] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const appliedConsentRef = useRef(null);

  useEffect(() => {
    if (appliedConsentRef.current === consent) return;
    appliedConsentRef.current = consent;

    if (consent === ANALYTICS_CONSENT_VALUES.GRANTED) {
      setAnalyticsReady(initializeAnalytics());
      setMixpanelReady(isMixpanelReady());
      return;
    }

    setAnalyticsReady(false);
    setMixpanelReady(false);
    if (
      consent === ANALYTICS_CONSENT_VALUES.DENIED ||
      consent === ANALYTICS_CONSENT_VALUES.UNKNOWN
    ) {
      disableAnalytics();
    }
  }, [consent]);

  const value = useMemo(
    () => ({
      consent,
      analyticsReady,
      mixpanelReady,
      isSettingsOpen,
      setAnalyticsConsent(nextConsent) {
        if (
          ![
            ANALYTICS_CONSENT_VALUES.GRANTED,
            ANALYTICS_CONSENT_VALUES.DENIED,
          ].includes(nextConsent)
        ) {
          return;
        }

        writeStoredConsent(nextConsent);
        setConsent(nextConsent);
        setIsSettingsOpen(false);
      },
      openAnalyticsSettings() {
        setIsSettingsOpen(true);
      },
      closeAnalyticsSettings() {
        setIsSettingsOpen(false);
      },
    }),
    [analyticsReady, consent, isSettingsOpen, mixpanelReady]
  );

  return (
    <AnalyticsConsentContext.Provider value={value}>
      {children}
    </AnalyticsConsentContext.Provider>
  );
}

export function useAnalyticsConsent() {
  const context = useContext(AnalyticsConsentContext);
  if (!context) {
    throw new Error(
      "useAnalyticsConsent must be used within AnalyticsConsentProvider"
    );
  }
  return context;
}
